import { setOpacity } from "../../../redux/slices/mapSlice";
import { CustomLogoClass } from "./CustomLogo";
import { CustomMarkerClass } from "./CustomMarker";
import { CustomShape } from "./CustomShape";
import { CustomTextBoxClass } from "./CustomTextbox";

export class CommonUtils {
  static UpdateVertexColorLayer = ({
    featureId,
    mapDraw,
    map,
    currentMode,
    polygonDefaultStyle,
  }) => {
    if (featureId) {
      let matchExpression = ["case"];
      let radiusMatchExpression = ["case"];
      let color;
      const allFeatures = mapDraw.getAll();
      const type = currentMode.current === "direct_select" ? "parent" : "id";
      allFeatures.features.forEach((feature) => {
        if (
          feature.geometry.type === "Polygon" ||
          feature.geometry.type === "LineString"
        ) {
          color =
            feature &&
            feature.properties &&
            feature.properties.vertexColor &&
            typeof feature.properties.vertexColor === "string"
              ? feature.properties.vertexColor
              : polygonDefaultStyle.polygonBorderColor || "#000000";
          matchExpression.push(
            [
              "==",
              ["get", featureId && featureId === feature.id ? type : "id"],
              feature.id,
            ],
            color
          );
        }
      });
      matchExpression.push(polygonDefaultStyle.polygonBorderColor); // Default color
      radiusMatchExpression.push(polygonDefaultStyle.polygonBorderWidth * 3); // Default radius
      const circleColorCold = map.getLayer(
        "gl-draw-polygon-and-line-vertex-active.cold"
      );
      const circleColorHot = map.getLayer(
        "gl-draw-polygon-and-line-vertex-active.cold"
      );
      if (circleColorCold) {
        map.setPaintProperty(
          "gl-draw-polygon-and-line-vertex-active.cold",
          "circle-color",
          matchExpression
        );
      }
      if (circleColorHot) {
        map.setPaintProperty(
          "gl-draw-polygon-and-line-vertex-active.hot",
          "circle-color",
          matchExpression
        );
      }
    }
  };

  static GetLeaderLine = ({ id, leaderLine }) => {
    const details = id.split("^");
    return {
      id: id,
      type: "Feature",
      properties: {
        "line-color": leaderLine.leaderLineColor,
        "line-width": 2,
        vertexColor: leaderLine.leaderLineWidth,
      },
      geometry: {
        coordinates: [
          [details[1], details[2]],
          [details[1], details[2]],
        ],
        type: "LineString",
      },
    };
  };

  static HideOrShowLeaderLine = ({
    mapDraw,
    connectedLeaderLine,
    correspondingElement,
    leaderLine,
    condition, // If true removes the leader line, else adds the leader line
    map,
  }) => {
    let lineConnected = connectedLeaderLine
      ? mapDraw.get(connectedLeaderLine)
      : null;
    if (condition) {
      if (lineConnected) {
        correspondingElement.dataset.leaderLine = JSON.stringify(lineConnected);
        mapDraw.delete(lineConnected.id); // Assuming 'id' is the property that identifies the feature
        if (map) {
          const vertexPoint = map.getLayer(lineConnected.id + "-end-point");
          if (vertexPoint) {
            map.removeLayer(lineConnected.id + "-end-point");
            map.removeSource(lineConnected.id + "-end-point");
          }
        }
      }
    } else {
      if (lineConnected) {
        return lineConnected;
      }
      if (correspondingElement.dataset.leaderLine) {
        lineConnected = JSON.parse(correspondingElement.dataset.leaderLine);
      } else {
        lineConnected = this.GetLeaderLine({
          id: correspondingElement.id,
          leaderLine,
        });
      }
      const lineConnectedProperties =
        lineConnected &&
        lineConnected.properties &&
        (!("line-color" in lineConnected.properties) ||
          !("line-width" in lineConnected.properties));

      if (lineConnectedProperties) {
        lineConnected.properties = {
          "line-color": leaderLine.leaderLineColor,
          "line-width": leaderLine.leaderLineWidth * 1,
        };
      }
      if (
        correspondingElement.dataset.leaderLineEnabled &&
        correspondingElement.dataset.leaderLineEnabled === "true"
      ) {
        mapDraw.add(lineConnected);
        if (lineConnectedProperties) {
          mapDraw.setFeatureProperty(
            connectedLeaderLine,
            "line-color",
            leaderLine.leaderLineColor
          );
          mapDraw.setFeatureProperty(
            connectedLeaderLine,
            "line-width",
            leaderLine.leaderLineWidth
          );
        }

        if (
          map &&
          JSON.stringify(lineConnected.geometry.coordinates[0]) !==
            JSON.stringify(lineConnected.geometry.coordinates[1])
        ) {
          const source = map.getSource(connectedLeaderLine + "-end-point");
          if (!source) {
            const icon = {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: lineConnected.geometry.coordinates[0],
              },
            };
            map.addSource(connectedLeaderLine + "-end-point", {
              type: "geojson",
              data: icon,
            });
          }
          map.addLayer({
            id: lineConnected.id + "-end-point",
            type: "circle",
            source: lineConnected.id + "-end-point",
            paint: {
              "circle-radius": leaderLine.leaderLineWidth + 5,
              "circle-color":
                lineConnected &&
                lineConnected.properties &&
                "line-color" in lineConnected.properties
                  ? lineConnected.properties["line-color"]
                  : leaderLine.leaderLineColor,
            },
          });
        }
      }
      if (
        correspondingElement.dataset.leaderLineEnabled &&
        correspondingElement.dataset.leaderLineEnabled === "true"
      ) {
        mapDraw.add(lineConnected);
      }
    }
    return lineConnected;
  };

  static RemoveMarkerFromList = ({ markerRef, id }) => {
    const updatedList = markerRef.current.filter((marker) => {
      if (marker.id === id) {
        marker.marker.remove();
      }
      return marker.id !== id;
    });
    markerRef.current = updatedList;
  };

  static stringToHTMLElement = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    return doc.body.firstChild;
  };

  static ApplyToMap = ({ map, mapData, getMapStyle, dispatch }) => {
    const isSatelliteView = mapData.style.layers.find(
      (item) => item.id === "satellite"
    );
    map.setStyle(mapData.style, { diff: false });
    map.once("styledata", () => {
      getMapStyle(mapData.mapLayer ? mapData.mapLayer : []);
      if (isSatelliteView) {
        dispatch(
          setOpacity(
            isSatelliteView.paint ? isSatelliteView.paint["raster-opacity"] : 1
          )
        );
      }
    });
    map.once("idle", () => {
      map.flyTo({
        center: mapData.center,
        zoom: mapData.zoom,
        bearing: mapData.bearing,
        pitch: mapData.pitch,
      });
    });
  };

  static RemoveMapBoxDrawLayersAndSources = (map) => {
    return new Promise((resolve, reject) => {
      // Get all existing layers and sources
      const allLayers = map.getStyle().layers || [];
      const allSources = map.getStyle().sources || {};

      // Remove draw layers
      allLayers.forEach((layer) => {
        if (layer.id.startsWith("gl-draw-")) {
          map.removeLayer(layer.id);
        }
      });

      // Remove draw sources
      Object.keys(allSources).forEach((sourceId) => {
        if (sourceId.startsWith("mapbox-gl-draw-")) {
          map.removeSource(sourceId);
        }
      });
      resolve();
    });
  };

  static ApplyDrawnFeaturesToMap = ({
    mapDraw,
    mapData,
    setActiveId,
    setDrawnFeaturesSupport,
  }) => {
    if (mapData.polygons) {
      mapData.polygons.features.forEach((mapDetails) => {
        setDrawnFeaturesSupport((previousValue) => {
          previousValue[mapDetails.id] = mapDetails;
          return previousValue;
        });
      });
      mapDraw.add(mapData.polygons);
    }

    mapDraw.changeMode("simple_select");
    setActiveId("none");
  };

  static GeoJSONToMarkers = ({
    markers,
    mapboxgl,
    map,
    mapDraw,
    resetAllOnceChanged,
    setTextBox,
    lastClickButton,
    logoDefaultStyle,
    updateAutoSave,
    setSelectedLogo,
    textBoxDefaultStyle,
    dispatch,
    setShapeSettings,
    setMapToolBarOptionFlags,
    setResetMap,
    createNewPolygon,
    currentMode,
    defaultLogoValue,
    poiSetting,
    polygonDefaultStyle,
    // setPointerSelected,
    getCurrentPoiSetting,
    isMapExport,
    setDrawnFeaturesSupport,
    setUndoHistory,
    setRedoHistory,
    showPointer,
    markerRef,
    // locationTradeAreaSettings,
  }) => {
    mapDraw = mapDraw.current || mapDraw;
    markers?.forEach((marker) => {
      if (marker.type === "textbox") {
        const textarea = this.stringToHTMLElement(marker.element);
        textarea.value = marker.value;
        //TDOO: Remove below code once halo effect added on textbox
        textarea.style.textShadow = "";

        CustomTextBoxClass.RecoverTextBox({
          coordinates: marker.coordinates,
          mapboxgl,
          map,
          mapDraw,
          textareaElement: textarea,
          resetAllOnceChanged,
          setTextBox,
          lastClickButton,
          updateAutoSave,
          textBoxDefaultStyle,
          setDrawnFeaturesSupport,
          setUndoHistory,
          setRedoHistory,
          disableUserActions: isMapExport,
          markerRef,
        });
      } else if (marker.type === "logo") {
        const logo = this.stringToHTMLElement(marker.element);
        if (logo.id.startsWith("emoji^")) {
          CustomLogoClass.RecoverEmojiFromMapData({
            coordinates: marker.coordinates,
            logoElement: logo,
            mapboxgl,
            map,
            mapDraw,
            lastClickButton,
            logoDefaultStyle,
            resetAllOnceChanged,
            setSelectedLogo,
            updateAutoSave,
            setMapToolBarOptionFlags,
            setResetMap,
            createNewPolygon,
            currentMode,
            defaultLogoValue,
            poiSetting,
            polygonDefaultStyle,
            getCurrentPoiSetting,
            setDrawnFeaturesSupport,
            setUndoHistory,
            setRedoHistory,
            disableUserActions: isMapExport,
            markerRef,
          });
        } else {
          CustomLogoClass.RecoverLogoFromMapData({
            coordinates: marker.coordinates,
            logoElement: logo,
            mapboxgl,
            map,
            mapDraw,
            lastClickButton,
            logoDefaultStyle,
            resetAllOnceChanged,
            setSelectedLogo,
            updateAutoSave,
            setMapToolBarOptionFlags,
            setResetMap,
            createNewPolygon,
            currentMode,
            defaultLogoValue,
            poiSetting,
            polygonDefaultStyle,
            getCurrentPoiSetting,
            disableUserActions: isMapExport,
            setDrawnFeaturesSupport,
            setUndoHistory,
            setRedoHistory,
            markerRef,
          });
        }
      } else if (marker.type === "marker") {
        const logo = this.stringToHTMLElement(marker.element);
        CustomMarkerClass.recoverMarkerFromSaveMap({
          map,
          markerElement: logo,
          coordinates: marker.coordinates,
          id: marker.id,
          markerRef,
        });
      } else if (marker.type === "labelText") {
        const pTag = this.stringToHTMLElement(marker.element);
        CustomMarkerClass.recoverLabelMarkerFromMapData({
          id: marker.id,
          coordinates: marker.coordinates,
          labelElement: pTag,
          map,
          updateAutoSave,
          dispatch,
          markerRef,
        });
      } else if (marker.type === "shape") {
        const iTag = this.stringToHTMLElement(marker.element);
        CustomShape.RecoverShapeToMap({
          coordinates: marker.coordinates,
          shapeTag: iTag,
          mapboxgl,
          map,
          lastClickButton,
          resetAllOnceChanged,
          setShapeSettings,
          disableUserActions: isMapExport,
          markerRef,
        });
      } else if (marker.type === "pointer") {
        const iTag = this.stringToHTMLElement(marker.element);
        iTag.id = marker.id;
        iTag.name = marker.name;
        CustomMarkerClass.RecoverPointerFromSaveMap({
          coordinates: marker.coordinates,
          pointerTag: iTag,
          mapboxgl,
          map,
          resetAllOnceChanged,
          updateAutoSave,
          showPointer,
          lastClickButton,
          disableUserActions: isMapExport,
          markerRef,
          dispatch,
        });
      }
    });
  };

  static ApplyAdditionalLayers = ({
    map,
    setHighways,
    setHighwaySelected,
    setPointOfInterest,
    setShowLayerAccOpen,
  }) => {
    const highwayLayer = map.getLayer("highway-layer");
    const poiLayer = map.getLayer("poi-layer");
    const poiLabel = map.getLayer("poi-label");
    if (
      highwayLayer &&
      highwayLayer.visibility &&
      highwayLayer.visibility === "visible"
    ) {
      const color = map.getPaintProperty("highway-layer", "line-color");
      const size = map.getPaintProperty("highway-layer", "line-width");
      const opacity = map.getPaintProperty("highway-layer", "line-opacity");
      setHighways(true);
      setHighwaySelected({
        enableSetting: false,
        highwayColor: color,
        highwaySize: size,
        highwayOpacity: opacity,
      });
      setShowLayerAccOpen(true);
    }
    if (
      (poiLayer && poiLayer.visibility && poiLayer.visibility === "visible") ||
      (poiLabel && poiLabel.visibility && poiLabel.visibility === "visible")
    ) {
      setPointOfInterest(true);
      setShowLayerAccOpen(true);
    }
  };

  static MarkersToGeoJSON = (markers) => {
    return markers.map((marker) => {
      const markerElement = marker.marker.getElement();
      const textarea = markerElement.querySelector("textarea");
      const image = markerElement.querySelector("img");
      const labelText = markerElement.querySelector("p");
      const iTag = markerElement.querySelector("i");
      const svgPointer = markerElement.querySelector("svg");

      if (marker.type === "textarea") {
        return {
          element: textarea.outerHTML,
          coordinates: marker.marker.getLngLat(),
          id: textarea.id,
          value: textarea.value,
          type: "textbox",
        };
      } else if (marker.type === "logo") {
        return {
          element: markerElement.outerHTML,
          coordinates: marker.marker.getLngLat(),
          id: markerElement.id,
          type: "logo",
        };
      } else if (marker.type === "marker") {
        return {
          element: image.outerHTML,
          coordinates: marker.marker.getLngLat(),
          id: marker.id,
          type: "marker",
        };
      } else if (marker.type === "paragraph") {
        return {
          element: labelText.outerHTML,
          coordinates: marker.marker.getLngLat(),
          id: labelText.id,
          type: "labelText",
        };
      } else if (["pointer", "shape"].includes(marker.type)) {
        if (marker.type === "pointer") {
          const data = svgPointer || iTag;
          return {
            element: data.outerHTML,
            coordinates: marker.marker.getLngLat(),
            id: marker.id,
            type: "pointer",
          };
        } else {
          return {
            element: iTag.outerHTML,
            coordinates: marker.marker.getLngLat(),
            id: iTag.id,
            type: "shape",
          };
        }
      } else {
        return {
          element: markerElement,
          coordinates: marker.marker.getLngLat(),
          id: marker.id,
          type: "marker",
        };
      }
    });
  };
}
